import React, { CSSProperties, useEffect, useRef } from 'react';
import styles from './ToastMessage.module.scss';
import Icon from '../Icon/Icon';

interface ToastMessageProps {
  message: string | null;
  subText?: string;
  toastType?: 'success' | 'warn' | 'error';
  showToast: boolean;
  setShowToast: (show: boolean) => void;
  customStyles?: CSSProperties;
  autoDismissal?: boolean;
  dismissTimeoutMs?: number;
}

const ToastMessage: React.FC<ToastMessageProps> = ({
  message,
  subText,
  toastType = 'success',
  showToast,
  setShowToast,
  customStyles,
  autoDismissal,
  dismissTimeoutMs = 3000,
}) => {
  const toastInterval = useRef<NodeJS.Timeout | null>(null);

  const clearToastTimeout = () => {
    if (toastInterval.current) {
      clearTimeout(toastInterval.current);
    }
  };

  // Dismissing the toast after 3 seconds
  useEffect(() => {
    if (!autoDismissal) return;
    if (showToast) {
      clearToastTimeout();
      toastInterval.current = setTimeout(() => {
        setShowToast(false);
      }, dismissTimeoutMs);
    }

    return () => clearToastTimeout();
  }, [showToast, setShowToast, dismissTimeoutMs, autoDismissal]);

  if (!showToast) return null;

  const toastIcons = {
    success: 'circle_tickmark_green',
    warn: 'warning_icon_orange',
    error: 'warning_icon_square',
  };

  return (
    <div className={styles.toast_msg_container} style={customStyles}>
      <div className={`${styles.toast_wrapper} ${styles[toastType]}`}>
        <div className={styles.toast_header}>
          <div className={styles.msg_section}>
            {toastIcons[toastType] && (
              <div className={styles.icon_section}>
                <Icon name={toastIcons[toastType]} size={16} />
              </div>
            )}
            <div className={styles.msg_txt}>{message}</div>
          </div>
          <div className={styles.close_icon} onClick={() => setShowToast(false)}>
            ✕
          </div>
        </div>
        {subText && <div className={styles.sub_text}>{subText}</div>}
      </div>
    </div>
  );
};

export default ToastMessage;
